/* ═══════════════════════════════════════════════════════════
   js/components/filter-panel.js — <filter-panel> Web Component
   Фильтры каталога: район, комнаты, статус, цена + badge-фильтры
   ═══════════════════════════════════════════════════════════ */
class FilterPanel extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._filters = {};
    this._debounceTimer = null;
    this._options = { districts: [], rooms: [], statuses: [] };
  }

  static get observedAttributes() { return ['data-city', 'data-fields']; }

  get city()   { return this.getAttribute('data-city') || 'kursk'; }
  get fields() { return (this.getAttribute('data-fields') || 'district,rooms,status,price').split(',').map(f => f.trim()); }

  connectedCallback() {
    this._render();
    this._loadOptions();
  }

  _esc(s) { const d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  attributeChangedCallback(name) {
    if (name === 'data-city') this._loadOptions();
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; }
        .panel { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
        select, input {
          background: var(--dark3, #1a1a24); border: 1px solid var(--border, rgba(255,255,255,.08));
          color: var(--text, #e8e6e1); padding: 10px 14px; border-radius: 10px;
          font-size: 13px; outline: none; min-width: 140px; cursor: pointer;
          font-family: var(--font-body, 'Mulish', sans-serif);
        }
        select:focus, input:focus { border-color: var(--gold, #c8a96e); }
        .badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
        .badge {
          background: var(--dark3, #1a1a24); border: 1px solid var(--border, rgba(255,255,255,.08));
          color: var(--text-muted, #8a8a8a); padding: 6px 14px; border-radius: 20px;
          font-size: 12px; cursor: pointer; transition: all .2s; user-select: none;
          font-family: var(--font-body, 'Mulish', sans-serif);
        }
        .badge:hover, .badge.active { border-color: var(--gold, #c8a96e); color: var(--gold, #c8a96e); background: rgba(200,169,110,.08); }
        .reset {
          background: none; border: 1px solid var(--border, rgba(255,255,255,.08));
          color: var(--text-muted, #8a8a8a); padding: 8px 14px; border-radius: 10px;
          font-size: 12px; cursor: pointer; transition: all .2s;
          font-family: var(--font-body, 'Mulish', sans-serif);
        }
        .reset:hover { color: var(--gold, #c8a96e); border-color: var(--gold, #c8a96e); }
        @media(max-width:768px) { .panel { flex-direction: column; } select, input { width: 100%; } }
      </style>
      <div class="panel" id="panel"></div>
      <div class="badges" id="badges"></div>
    `;
  }

  async _loadOptions() {
    try {
      const res = await fetch('/map_api.php?action=filters&city=' + encodeURIComponent(this.city));
      if (res.ok) {
        const data = await res.json();
        if (data.ok) {
          this._options = data;
          this._buildControls();
        }
      }
    } catch (e) {
      // Fallback — пустые фильтры
      this._buildControls();
    }
  }

  _buildControls() {
    const panel = this.shadowRoot.getElementById('panel');
    const fields = this.fields;
    let html = '';

    if (fields.includes('district')) {
      html += '<select data-filter="district"><option value="">Все районы</option>';
      (this._options.districts || []).forEach(d => {
        html += '<option value="' + this._esc(d) + '">' + this._esc(d) + '</option>';
      });
      html += '</select>';
    }

    if (fields.includes('rooms')) {
      html += '<select data-filter="rooms"><option value="">Комнатность</option>';
      (this._options.rooms || ['Студия', '1', '2', '3', '4+']).forEach(r => {
        html += '<option value="' + this._esc(r) + '">' + (r === 'Студия' ? 'Студия' : this._esc(r) + '-комн') + '</option>';
      });
      html += '</select>';
    }

    if (fields.includes('status')) {
      html += '<select data-filter="status"><option value="">Все статусы</option>';
      (this._options.statuses || ['building', 'ready']).forEach(s => {
        const label = s === 'building' ? 'Строится' : s === 'ready' ? 'Сдан' : this._esc(s);
        html += '<option value="' + this._esc(s) + '">' + label + '</option>';
      });
      html += '</select>';
    }

    if (fields.includes('price')) {
      html += '<input type="number" data-filter="price_from" placeholder="Цена от" min="0" step="100000">';
      html += '<input type="number" data-filter="price_to" placeholder="Цена до" min="0" step="100000">';
    }

    html += '<button class="reset" data-action="reset">Сбросить</button>';

    panel.innerHTML = html;

    // Badges
    const badges = this.shadowRoot.getElementById('badges');
    const badgeList = this._options.badges || ['Льготная ипотека', 'Сертификат', 'Рассрочка', 'Скидка'];
    badges.innerHTML = badgeList.map(b =>
      '<span class="badge" data-badge="' + this._esc(b) + '">' + this._esc(b) + '</span>'
    ).join('');

    // Events
    panel.querySelectorAll('select, input').forEach(el => {
      el.addEventListener('change', () => this._emitChange());
      el.addEventListener('input', () => this._emitChangeDebounced());
    });

    panel.querySelector('[data-action="reset"]').addEventListener('click', () => this._reset());

    badges.querySelectorAll('.badge').forEach(el => {
      el.addEventListener('click', () => {
        el.classList.toggle('active');
        this._emitChange();
      });
    });
  }

  _emitChangeDebounced() {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => this._emitChange(), 300);
  }

  _emitChange() {
    const filters = {};
    this.shadowRoot.querySelectorAll('[data-filter]').forEach(el => {
      if (el.value) filters[el.dataset.filter] = el.value;
    });

    const badges = [];
    this.shadowRoot.querySelectorAll('.badge.active').forEach(el => {
      badges.push(el.dataset.badge);
    });
    if (badges.length) filters.badges = badges;

    this._filters = filters;
    this.dispatchEvent(new CustomEvent('filter-change', {
      bubbles: true,
      detail: filters
    }));
  }

  _reset() {
    this.shadowRoot.querySelectorAll('select').forEach(el => { el.value = ''; });
    this.shadowRoot.querySelectorAll('input[type="number"]').forEach(el => { el.value = ''; });
    this.shadowRoot.querySelectorAll('.badge').forEach(el => { el.classList.remove('active'); });
    this._filters = {};
    this._emitChange();
  }

  getFilters() { return { ...this._filters }; }
}

if (!customElements.get('filter-panel')) {
  customElements.define('filter-panel', FilterPanel);
}
